import Error from "next/error";
import { ContextProvider } from "../../../../components/context-provider";
import Layout from "../../../../components/layout";
import { getSSRProps } from "../../../../lib/api/content/render";
import { log } from "../../../../lib/utils/commons";
import BodyContent from "../../../../components/BodyContent";
import getRawBody from "raw-body";
import { validateRequest } from "../../../../lib/api/content/validation";
import { getLocale } from "../../../../lib/utils/http-utils";
import { pushMessageToQueue } from "../../../../services/aws/sqs";
import { APP_ENV, getConfig } from "../../../../config";
import { getComparatorQueueBody } from "../../../../lib/utils/workflow-utils";

const getErrorBlanket = (statusCode) => {
  return <Error statusCode={statusCode} />;
};

// A react component cannot return as a promise so you don't place the async on this function
const OrchPost = ({
  blocks,
  metaData,
  externalData,
  statusCode,
  logTrace,
  urlParamsProps
}) => {
  logTrace += `;STATUS=${statusCode}`;
  log.info(`${logTrace};EVENT_NAME=EVENT_RENDER_POST_INITIATED`);
  let err;

  if (statusCode !== 200) {
    err = getErrorBlanket(statusCode);
  }

  const event_name = err
    ? "EVENT_RENDER_POST_ERROR"
    : "EVENT_RENDER_POST_COMPLETED";
  log.info(`${logTrace};EVENT_NAME=${event_name}`);

  return (
    <Layout metaData={metaData}>
      <ContextProvider metaData={metaData}>
        <BodyContent
          blocks={blocks}
          externalData={externalData}
          logTrace={logTrace}
          err={err}
          metaData={metaData}
          urlParamsProps={urlParamsProps}
        />
      </ContextProvider>
    </Layout>
  );
};

/**
 * Parses a JSON string and returns the resulting object.
 * Logs an error and returns an empty object if parsing fails.
 *
 * @param {any} body - The JSON string to parse.
 * @param {any} query - query
 * @returns {Object} The parsed object or {} on failure.
 */
const parseJsonBody = (body, query) => {
  let postBody = {};
  try {
    postBody = body ? JSON.parse(body?.toString("utf-8")) : {};
  } catch (jsonparseerror) {
    // in case of any json parsing error, default to blank JSON
    log.error(
      `{error : "${jsonparseerror}", query : "${query}", event : "POSTBODY_JSON_PARSE_ERROR_DEFAULTING_TO_{}"}`
    );
    postBody = {};
  }
  return postBody;
};

// This is a NextJS extension of functional components
// Here we can get all info that we want passed into the component via props

export async function getServerSideProps({ query, req, res }) {
  // isOrchestration will be true for a normal request
  // under /pages/page/render/cas/* (more specific path)
  // Since NextJs doenst have handler to extract a POST request body
  // under the /pages/page/* paths, we have to use a different
  // mechainsm to extract the POST body data.
  const isOrchestration = true;
  const { casid, variation } = query;
  const body = await getRawBody(req);
  const postBody = parseJsonBody(body, query);
  const locale = getLocale({ query }, req.headers, false, "");

  // If notify  is false then do not send event else assume notify is by default true and send the event
  // After peek we need to enable this again
  // const notify = query.notify !== undefined ? query.notify === "true" : true;
  const notify = false;
  try {
    // TODO: enable this once weborc team fixes bug on their end
    validateRequest(casid, locale, postBody);
  } catch (error) {
    if (error?.statusCode === 400) {
      res.statusCode = 400;
    }
    return { props: { statusCode: res.statusCode } };
  }
  const ssrProps = await getSSRProps(
    { query, req, res },
    isOrchestration,
    postBody,
    casid
  );
  // it will notify to comparator queue
  if (notify) {
    await handleQueue(ssrProps, postBody, variation);
  }

  return ssrProps;
}

async function handleQueue(ssrProps, postBody, variation) {
  try {
    const url = getConfig("raas_comparator_queue");
    const queueBody = getComparatorQueueBody(ssrProps, postBody, variation);
    const IAM_ASSUME_ROLE = getConfig("IAM_COMPARATOR_ASSUME_ROLE");

    const response = await pushMessageToQueue(
      queueBody,
      url,
      ssrProps?.props?.logTrace,
      IAM_ASSUME_ROLE
    );

    log.info(
      `${ssrProps?.props?.logTrace};EVENT_NAME=${APP_ENV}-comparator-queue and RESPONSE =${response}`
    );
  } catch (error) {
    log.error(
      `${ssrProps?.props?.logTrace};EVENT_NAME=${APP_ENV}-comparator-queue_failed Error occurred while pushing Message to live SQS - ${error.stack}`
    );
  }
}

export default OrchPost;
